import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  distinctUntilChanged,
  filter,
  map,
  merge,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { PICFLOW_CONFIG } from '../picflow-config';
import { AssetService } from './asset.service';
import { GalleryPasswordService } from './gallery-password.service';

@Injectable({
  providedIn: 'root',
})
export class AssetApprovalLimitService {
  private assetsService = inject(AssetService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private config = inject(PICFLOW_CONFIG);
  private http = inject(HttpClient);

  private currentGalleryId$ = new BehaviorSubject<string>(undefined);

  private approvedAssetCount$: Observable<{ galleryId: string; approvedAssetsCount: number }> =
    this.currentGalleryId$.pipe(
      distinctUntilChanged(),
      filter(Boolean),
      switchMap(galleryId => {
        const url = addGalleryPassword(
          `${this.config.apiEndpoint}/assets?gallery=${galleryId}&select[]=id&status[]=approved`,
          this.galleryPasswordService.getPassword(galleryId)
        );
        return this.http
          .get<{ data: { id: string }[] }>(url, {
            transferCache: false,
          })
          .pipe(
            map(resp => resp.data.map(d => d.id)),
            switchMap(assetIds => {
              return merge(
                this.assetsService.onAssetUpdated$.pipe(
                  filter(event => {
                    if (!event?.asset) return false;
                    if (event.asset.gallery !== galleryId) return false;
                    return true;
                  })
                ),
                this.assetsService.onAssetDeleted$.pipe(
                  filter(event => {
                    if (!event?.asset) return false;
                    if (event.asset.gallery !== galleryId) return false;
                    return true;
                  })
                )
              ).pipe(
                startWith(undefined),
                map(event => {
                  if (!event) return { galleryId, approvedAssetsCount: assetIds.length };
                  if (event.name === 'deleted') {
                    assetIds = assetIds.filter(id => id !== event.asset.id);
                  }
                  if (event.name === 'updated') {
                    const index = assetIds.indexOf(event.asset.id);
                    if (index === -1) {
                      if (event.asset.status === 'approved') {
                        assetIds.push(event.asset.id);
                      }
                    } else {
                      if (event.asset.status !== 'approved') {
                        assetIds.splice(index, 1);
                      }
                    }
                  }
                  return { galleryId, approvedAssetsCount: assetIds.length };
                })
              );
            })
          );
      }),
      shareReplay(1)
    );

  public getApprovedAssetCountForGallery(galleryId: string): Observable<number> {
    this.currentGalleryId$.next(galleryId);
    return this.approvedAssetCount$.pipe(
      filter(r => r.galleryId === galleryId),
      map(r => r.approvedAssetsCount)
    );
  }
}
